import React, { useEffect } from 'react';
import { Form, Input, Modal, Select } from 'antd';
import useIsMobile from '@/hooks/useIsMobile';
import { useApp } from 'contexts/app';
import UserService from 'services/collaborator_user';
import UserListDesktop from './desktop';
import UserListMobile from './mobile';
import { permissions, useUserList } from './useUserList';

const service = new UserService();

const UserListPage = () => {
  const isMobile = useIsMobile(992);
  const viewModel = useUserList();

  return (
    <>
      {isMobile ? <UserListMobile viewModel={viewModel} /> : <UserListDesktop viewModel={viewModel} />}
      <AddNewUser open={viewModel.showAddUser} onClose={() => viewModel.setShowAddUser(false)} />
      <UpdateUser
        open={viewModel.showUpdateUser}
        onClose={() => viewModel.setShowUpdateUser(false)}
        user={viewModel.updateUser}
        onSaved={viewModel.loadData}
      />
    </>
  );
};

const AddNewUser = ({ open, onClose }) => {
  const [form] = Form.useForm();
  const { showLoading, showNotify } = useApp();

  const onFinish = async () => {
    const data = { ...form.getFieldsValue(), role: 'ctv', permissions: [] };
    showLoading(true);
    try {
      await service.invite(data);
      showNotify('Đã gửi lời mời');
      onClose();
    } finally {
      showLoading(false);
    }
  };

  return (
    <Modal open={open} title='Mời cộng tác viên' onCancel={onClose} onOk={() => form.submit()}>
      <Form layout='vertical' form={form} initialValues={{}} onFinish={onFinish}>
        <Form.Item
          label='Số điện thoại'
          name='phone'
          rules={[
            {
              required: true,
              pattern: /^[0-9]{10}$/,
              message: 'Số điện thoại không đúng định dạng',
            },
            {
              validator: async (_, value) => {
                return new Promise(async (resolve, reject) => {
                  try {
                    const user = await service.checkPhoneCanInvite(value);
                    if (user) {
                      form.setFieldValue('user_id', user.id);
                      resolve();
                    } else {
                      form.setFieldValue('user_id', null);
                      reject(new Error('Số điện thoại này đã chưa được đăng ký'));
                    }
                  } catch (error) {
                    reject(new Error('Không thể kiểm tra số điện thoại'));
                  }
                });
              },
            },
          ]}
        >
          <Input placeholder='Phone' />
        </Form.Item>
        <Form.Item hidden name='user_id'>
          <Input placeholder='Phone' />
        </Form.Item>
      </Form>
    </Modal>
  );
};

const UpdateUser = ({ open, onClose, user, onSaved }) => {
  const [form] = Form.useForm();
  const { showLoading, showNotify } = useApp();

  useEffect(() => {
    if (user) {
      form.setFieldsValue(user.work_space);
    } else {
      form.setFieldsValue({});
    }
  }, [open, user, form]);

  const onFinish = async () => {
    const data = { ...form.getFieldsValue(), user_id: user.id };
    showLoading(true);
    try {
      await service.updateUserRole(data);
      showNotify('Đã cập nhật quyền');
      onClose();
      onSaved();
    } finally {
      showLoading(false);
    }
  };

  return (
    <Modal open={open} title='Cập nhật quyền người dùng' onCancel={onClose} onOk={() => form.submit()}>
      <Form layout='vertical' form={form} initialValues={{}} onFinish={onFinish}>
        <UserFields />
      </Form>
    </Modal>
  );
};

const UserFields = () => {
  return (
    <>
      <Form.Item
        label='Vai trò'
        name='role'
        rules={[
          {
            required: true,
          },
        ]}
      >
        <Select
          options={[
            { label: 'Quản trị viên', value: 'admin' },
            { label: 'Nhân viên', value: 'staff' },
          ]}
        />
      </Form.Item>

      <Form.Item
        label='Phân quyền'
        name='permissions'
        rules={[
          {
            required: true,
            message: 'Bạn phải chọn 1 quyền',
          },
        ]}
      >
        <Select options={permissions} mode='multiple' />
      </Form.Item>
    </>
  );
};

export default React.memo(UserListPage);
